import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { applyTheme, GRAPHIKPRO_TOKENS } from "@/context/ThemeProvider";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Command, Loader2 } from "lucide-react";

const DEMO = [
  { label: "Graphikpro super-admin", email: "admin@graphikpro.io", password: "Graphikpro!2026" },
  { label: "VGR Rentals owner", email: "owner@vgr.app", password: "VgrOwner!2026" },
  { label: "Velocity Rides owner", email: "owner@velocity.app", password: "VelocityOwner!2026" },
  { label: "VGR mechanic", email: "mechanic@vgr.app", password: "VgrMech!2026" },
];

const homeFor = (role) =>
  role === "super_admin" ? "/admin" : role === "mechanic" ? "/mechanic" : "/app";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { applyTheme(GRAPHIKPRO_TOKENS); }, []);
  useDocumentTitle("Sign in");

  useEffect(() => {
    if (user) navigate(homeFor(user.role), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
    else navigate(homeFor(res.user.role), { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-tenant-bg">
      <div className="hidden flex-1 flex-col justify-between bg-tenant-primary p-12 text-tenant-primary-foreground lg:flex">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Command className="h-6 w-6" /> FleetOS <span className="opacity-60">by Graphikpro</span>
        </div>
        <div>
          <h1 className="text-4xl font-bold leading-tight tracking-tight">
            The operational backbone rental operators can't build themselves.
          </h1>
          <p className="mt-4 max-w-md text-base opacity-70">
            Multi-tenant, white-label. Gated pipeline, risk engine and audit trail — one codebase,
            every operator branded as their own.
          </p>
        </div>
        <p className="text-sm opacity-50">Phase 0 · Multi-tenant foundation</p>
      </div>

      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <h2 className="text-2xl font-semibold tracking-tight">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Platform & operator access</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@operator.app" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full gap-2 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
            </Button>
          </form>

          <div className="mt-8 rounded-lg border border-border p-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Demo accounts (click to fill)</p>
            <div className="space-y-1.5">
              {DEMO.map((d) => (
                <button key={d.email} type="button" data-testid={`demo-${d.email}`}
                  onClick={() => { setEmail(d.email); setPassword(d.password); }}
                  className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted">
                  <span className="font-medium">{d.label}</span>
                  <span className="text-muted-foreground">{d.email}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
